import type { PharmaVariantMetadata, ProductSection } from '@lib/types/pharma';

export type ValidatedSection = { label: string; content: string };

/** Strict validator for product-level sections (requires explicit label + content). */
export function isValidSection(s: unknown): s is ValidatedSection {
  const section = s as ProductSection | null | undefined;
  return !!section?.label?.trim() && !!section?.content?.trim();
}

function extractHeadingLabel(content: string): string | null {
  const firstLine = content.trimStart().split('\n')[0];
  const match = firstLine.match(/^#{1,6}\s+(.+)$/);
  return match ? match[1].trim() : null;
}

/**
 * Permissive parser for variant-level sections.
 * Accepts objects with explicit label+content, objects with only content
 * (label extracted from the first markdown heading), and plain strings.
 */
// fallow-ignore-next-line complexity
export function parseVariantSection(s: unknown): ValidatedSection | null {
  let content: string | undefined;
  let explicitLabel: string | undefined;

  if (typeof s === 'string') {
    content = s.trim();
  } else if (typeof s === 'object' && s !== null) {
    const sec = s as ProductSection;
    content = sec.content?.trim();
    explicitLabel = sec.label?.trim();
  }

  if (!content) return null;

  const label = explicitLabel || extractHeadingLabel(content);
  if (!label) return null;

  return { label, content };
}

export type VariantSectionField = {
  key: keyof Pick<
    PharmaVariantMetadata,
    | 'benefits'
    | 'directions_for_use'
    | 'contraindications'
    | 'side_effects'
    | 'pregnancy_breastfeeding_guidance'
    | 'pil_url'
  >;
  label: string;
  id: string;
};

export const VARIANT_SECTION_FIELDS: readonly VariantSectionField[] = [
  { key: 'benefits', label: 'Benefits', id: 'benefits' },
  {
    key: 'directions_for_use',
    label: 'Directions for Use',
    id: 'directions-for-use',
  },
  {
    key: 'contraindications',
    label: 'Contraindications',
    id: 'contraindications',
  },
  { key: 'side_effects', label: 'Side Effects', id: 'side-effects' },
  {
    key: 'pregnancy_breastfeeding_guidance',
    label: 'Pregnancy & Breastfeeding',
    id: 'pregnancy-breastfeeding-guidance',
  },
  {
    key: 'pil_url',
    label: 'Patient Information Leaflet',
    id: 'patient-information-leaflet',
  },
];

function normalizeSections(sections: unknown): unknown[] {
  if (typeof sections === 'string') return [sections];
  if (Array.isArray(sections)) return sections;
  return [];
}

/** All sections from a variant's metadata (no filtering). */
export function getVariantSections(
  meta: PharmaVariantMetadata | undefined,
): ValidatedSection[] {
  const raw = normalizeSections(meta?.sections);
  if (raw.length === 0) return [];
  return raw.flatMap((s) => {
    const parsed = parseVariantSection(s);
    return parsed ? [parsed] : [];
  });
}

/**
 * Merge variant sections with product sections.
 * Variant sections take precedence for matching labels (variant-specific content
 * overrides the generic product version). Product sections without a matching
 * variant section are shown as-is.
 */
export function mergeSections(
  variantSections: ValidatedSection[],
  productSections: ValidatedSection[],
): ValidatedSection[] {
  const variantLabels = new Set(variantSections.map((s) => s.label));
  const remainingProduct = productSections.filter(
    (s) => !variantLabels.has(s.label),
  );
  return [...variantSections, ...remainingProduct];
}

export function getVariantFields(
  meta: PharmaVariantMetadata | undefined,
): Array<{ field: VariantSectionField; value: string }> {
  return VARIANT_SECTION_FIELDS.flatMap((field) => {
    const val = meta?.[field.key];
    return typeof val === 'string' && val.trim().length > 0
      ? [{ field, value: val }]
      : [];
  });
}
